import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Education } from '../../types';
import { formatMonth, toDateInput, toNumberOrNull, toStringOrNull } from '../../utils/format';
import ConfirmDialog from '../common/ConfirmDialog';

interface Props {
  items: Education[];
  onCreate: (data: Partial<Education>) => Promise<void>;
  onUpdate: (id: Education['id'], data: Partial<Education>) => Promise<void>;
  onDelete: (id: Education['id']) => Promise<void>;
}

interface FormState {
  school: string;
  degree: string;
  field_of_study: string;
  start_date: string;
  end_date: string;
  gpa: string;
  gpa_scale: string;
}

const EMPTY: FormState = {
  school: '',
  degree: '',
  field_of_study: '',
  start_date: '',
  end_date: '',
  gpa: '',
  gpa_scale: '',
};

function toForm(item: Education): FormState {
  return {
    school: item.school ?? '',
    degree: item.degree ?? '',
    field_of_study: item.field_of_study ?? '',
    start_date: toDateInput(item.start_date),
    end_date: toDateInput(item.end_date),
    gpa: item.gpa === null || item.gpa === undefined ? '' : String(item.gpa),
    gpa_scale: item.gpa_scale === null || item.gpa_scale === undefined ? '' : String(item.gpa_scale),
  };
}

function toPayload(form: FormState): Partial<Education> {
  return {
    school: form.school.trim(),
    degree: form.degree.trim(),
    field_of_study: form.field_of_study.trim(),
    start_date: toStringOrNull(form.start_date),
    end_date: toStringOrNull(form.end_date),
    gpa: toNumberOrNull(form.gpa),
    gpa_scale: toNumberOrNull(form.gpa_scale),
  };
}

const EducationSection: React.FC<Props> = ({ items, onCreate, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState<{ id: Education['id'] | null; form: FormState } | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Education | null>(null);
  const [deleting, setDeleting] = useState(false);

  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setEditing((s) => (s ? { ...s, form: { ...s.form, [key]: e.target.value } } : s));

  const canSave = Boolean(editing?.form.school.trim()) && !saving;

  const handleSave = async () => {
    if (!editing || !canSave) return;
    setSaving(true);
    try {
      const payload = toPayload(editing.form);
      if (editing.id === null) {
        await onCreate({ ...payload, ordering: items.length });
      } else {
        await onUpdate(editing.id, payload);
      }
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await onDelete(pendingDelete.id);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Education
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setEditing({ id: null, form: EMPTY })}
        >
          Add education
        </Button>
      </Stack>

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No education added yet. Add your schools so applications can be filled from them.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {items.map((item) => (
            <Card key={item.id} variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {item.school}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[item.degree, item.field_of_study].filter(Boolean).join(', ')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[formatMonth(item.start_date), formatMonth(item.end_date) || (item.start_date ? 'Present' : '')]
                        .filter(Boolean)
                        .join(' - ')}
                      {item.gpa !== null && item.gpa !== undefined && item.gpa !== ''
                        ? ` | GPA ${item.gpa}${item.gpa_scale ? ` / ${item.gpa_scale}` : ''}`
                        : ''}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditing({ id: item.id, form: toForm(item) })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setPendingDelete(item)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={Boolean(editing)} onClose={() => !saving && setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.id === null ? 'Add education' : 'Edit education'}</DialogTitle>
        <DialogContent>
          {editing && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <TextField label="School" value={editing.form.school} onChange={set('school')} fullWidth required autoFocus />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Degree" value={editing.form.degree} onChange={set('degree')} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Field of study" value={editing.form.field_of_study} onChange={set('field_of_study')} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Start date" type="date" value={editing.form.start_date} onChange={set('start_date')} fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="End date" type="date" value={editing.form.end_date} onChange={set('end_date')} fullWidth InputLabelProps={{ shrink: true }} helperText="Leave blank if in progress" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="GPA" type="number" inputProps={{ step: 0.01, min: 0 }} value={editing.form.gpa} onChange={set('gpa')} fullWidth />
              </Grid>
              <Grid item xs={6}>
                <TextField label="GPA scale" type="number" inputProps={{ step: 0.1, min: 0 }} value={editing.form.gpa_scale} onChange={set('gpa_scale')} fullWidth placeholder="4.0" />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete this education entry?"
        message={pendingDelete ? `${pendingDelete.school} will be removed from your profile.` : undefined}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Box>
  );
};

export default EducationSection;
